/**
 * The open-mode decision (1.I-2 deliverable 1), pure. The kit's `openModeFor` order holds
 * (Mod, then Alt, then Shift); a plain activation opens the page inside a `nav` landmark (the
 * sidebar, the breadcrumbs) and a side peek anywhere else (open point 3).
 */
import type { OpenMode } from '@codex/contracts';

export type NavSource = 'main' | 'side' | 'center' | 'split';

export interface DecideEvent {
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly button?: number | undefined;
  readonly key?: string | undefined;
}

export interface DecideContext {
  /** Whether Mod is the Command key (Apple) rather than Control. */
  readonly apple: boolean;
  /** Whether the trigger sits in a `nav` landmark. */
  readonly inNav: boolean;
}

/** `'browser'` leaves the event to the browser (a middle click, a key that is not Enter). */
export function decideOpen(event: DecideEvent, context: DecideContext): 'browser' | OpenMode {
  if (event.button !== undefined && event.button !== 0) return 'browser';
  if (event.key !== undefined && event.key !== 'Enter') return 'browser';
  const mod = context.apple ? event.metaKey : event.ctrlKey;
  const otherMod = context.apple ? event.ctrlKey : event.metaKey;
  // Control-click on an Apple platform is a context menu: not ours.
  if (otherMod) return 'browser';
  if (mod) return 'full';
  if (event.altKey) return 'center';
  if (event.shiftKey) return 'split';
  return context.inNav ? 'full' : 'peek';
}

const SOURCES: readonly NavSource[] = ['side', 'center', 'split'];

/** The pane an element sits in: its nearest `[data-nav-pane]` ancestor, else the page. */
export function sourceOf(element: Element | null): NavSource {
  const value = element?.closest('[data-nav-pane]')?.getAttribute('data-nav-pane');
  return SOURCES.find((s) => s === value) ?? 'main';
}
